"use client";

import Field from "@client/components/formik/field";
import Input from "@client/components/formik/input";
import PhoneNumber from "@client/components/formik/phone-number";
import Fieldset from "@client/components/lead-form/components/fieldset";

import { postEvent, validatePhoneNumber } from "@client/functions/client-actions";
import {
	PHONE_NUMBER_ERROR_MESSAGE,
	setUserInteractionDataAttribute,
} from "@shared/constants";
import kebabToCamelCase from "@shared/functions/kebabToCamelCase";
import sleep from "@shared/functions/sleep";
import { FormButton } from "@ui/js/components/buttons/form-button";
import { useField, useFormikContext } from "formik";

const INPUT_ID_AND_LABEL_HTMLFOR = "phone-number";
const FIELD_NAME_SELECTOR = kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR);

export default function Phone() {
	const { submitCount, setStatus } = useFormikContext();

	const [_, meta] = useField(FIELD_NAME_SELECTOR);

	async function phoneNumberValidation(phoneNumber: string) {
		// The error message to display to the user
		let errorMessage;

		// If the previous submit had an invalid formatted phone number error from the top-level validation
		const previousSubmitHadInvalidFormat =
			meta.error === PHONE_NUMBER_ERROR_MESSAGE;

		/**
		 * Skip Twilio validation if:
		 *  - The form has been submitted more than once.
		 *  - AND The previous submit did not have an invalid format error.
		 */
		if (submitCount > 0 && !previousSubmitHadInvalidFormat) {
			return ""; // No validation error
		}

		// Reset the lead form status via Formik
		setStatus("");

		// Validate the phone number using Twilio
		const { validPhoneNumber, timedOut } =
			await validatePhoneNumber(phoneNumber);

		postEvent("phone_validation", {
			wizard: {
				message: `Validation for phone number '${phoneNumber}' returned '${validPhoneNumber}'.`,
				validPhoneNumber: validPhoneNumber,
				step: {
					flow: "lead-form",
					step_name: "phone",
					step_number: 99, // TODO: Update this to the correct step number
				},
			},
		});

		// If the phone number is valid or the request timed out, return no error
		if (validPhoneNumber === true || timedOut === true) {
			return "";
		} else if (validPhoneNumber === false) {
			// Set the lead form status to "warning" via Formik
			setStatus("warning");

			postEvent("error", {
				error: {
					error_message: `Validation for phone number '${phoneNumber}' returned '${validPhoneNumber}'.`,
					error_object: "Lead Form",
					error_type: "twilio_validation",
					path: window.location.href,
					invalid_phone_number: phoneNumber,
				},
			});

			errorMessage =
				"The entered phone number appears to be invalid.  Please verify it is correct.";
		} else {
			// Set the lead form status to "error" via Formik
			setStatus("error");

			postEvent("error", {
				error: {
					error_message: `An error occurred while attempting to validate the phone number '${phoneNumber}'.`,
					error_object: "Lead Form",
					error_type: "twilio_validation",
					path: window.location.href,
					invalid_phone_number: phoneNumber,
				},
			});

			errorMessage =
				"An error occurred while validating the phone number. Please try again.";
		}

		// Add an artificial delay to allow loading spinner to display for a moment
		await sleep(300);

		return errorMessage;
	}

	return (
		<>
			<Input type="hidden" name="agreesToTCPA" id="agrees-to-tcpa" />
			<Fieldset
				dialog="Your phone number enables direct communication for timely updates and support regarding your debt solutions.  We ask this so that we, our partners and their affiliated companies can contact you about the products and services you inquired about, even if your telephone number is listed on any Do-Not Call list. Contact may be made through automatic dialing systems, artificial or prerecorded voice messaging, or text message."
				dialogHeader="Why do we need your phone number?"
				legend="What is your phone number?"
			>
				<Field
					{...setUserInteractionDataAttribute(
						`lead-form_step_${INPUT_ID_AND_LABEL_HTMLFOR}`,
					)}
					id={INPUT_ID_AND_LABEL_HTMLFOR}
					name={FIELD_NAME_SELECTOR}
					label="Phone Number"
					validate={phoneNumberValidation}
				>
					<PhoneNumber
						id={INPUT_ID_AND_LABEL_HTMLFOR}
						name={FIELD_NAME_SELECTOR}
					/>
				</Field>
			</Fieldset>
			<FormButton
				{...setUserInteractionDataAttribute(
					`lead-form_submit-button_${INPUT_ID_AND_LABEL_HTMLFOR}`,
				)}
			/>
		</>
	);
}
